import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

export const Carousel = ({ items, selectedId, onSelect, className, footer }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  // Update current index when selected item changes
  useEffect(() => {
    const index = items.findIndex(item => item.id === selectedId);
    if (index !== -1 && index !== currentIndex) {
      setCurrentIndex(index);
    }
  }, [selectedId, items, currentIndex]);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      rotateY: direction > 0 ? 45 : -45,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      rotateY: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction > 0 ? -1000 : 1000,
      rotateY: direction > 0 ? -45 : 45,
      opacity: 0,
    }),
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    // Enable infinite scrolling with rollover
    let newIndex = currentIndex + newDirection;
    
    if (newIndex < 0) {
      newIndex = items.length - 1; // Roll to end
    } else if (newIndex >= items.length) {
      newIndex = 0; // Roll to beginning
    }
    
    setDirection(newDirection);
    setCurrentIndex(newIndex);
    onSelect(items[newIndex].id);
  };

  const handleDotClick = (index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
    onSelect(items[index].id);
  };

  // Helper function to get item at index with rollover
  const getItemAtIndex = (offset) => {
    const index = (currentIndex + offset + items.length) % items.length;
    return items[index];
  };

  if (!items || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const prevItem = getItemAtIndex(-1);
  const nextItem = getItemAtIndex(1);

  return (
    <div className={cn("relative", className)}>
      {/* Carousel Container */}
      <div className="relative overflow-hidden rounded-lg bg-neutral-50 min-h-[280px] sm:min-h-[340px]" style={{ perspective: '1200px' }}>
        {/* Previous Item Preview */}
        {items.length > 1 && (
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 flex items-center justify-start pl-2 sm:pl-4 z-0 opacity-40 pointer-events-none">
            {prevItem.imageUrl && (
              <img
                src={prevItem.imageUrl}
                alt=""
                className="w-12 sm:w-20 h-24 sm:h-32 object-cover rounded-md shadow-sm"
                aria-hidden="true"
              />
            )}
          </div>
        )}

        {/* Next Item Preview */}
        {items.length > 1 && (
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 flex items-center justify-end pr-2 sm:pr-4 z-0 opacity-40 pointer-events-none">
            {nextItem.imageUrl && (
              <img
                src={nextItem.imageUrl}
                alt=""
                className="w-12 sm:w-20 h-24 sm:h-32 object-cover rounded-md shadow-sm"
                aria-hidden="true"
              />
            )}
          </div>
        )}

        {/* Main Carousel */}
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              rotateY: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.3 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            className="absolute w-full h-full flex flex-col items-center justify-center p-2 px-12 sm:px-14 cursor-grab active:cursor-grabbing"
            style={{ pointerEvents: 'none' }}
          >
            <div style={{ pointerEvents: 'auto' }} className="w-full">
              {/* Image */}
              {currentItem.imageUrl && (
                <div className="w-full mx-auto mb-2">
                  <img
                    src={currentItem.imageUrl}
                    alt={currentItem.label}
                    className="w-full h-44 sm:h-52 object-cover rounded-lg shadow-md"
                    loading={currentIndex === 0 ? undefined : 'lazy'}
                    fetchpriority={currentIndex === 0 ? 'high' : undefined}
                    width="1024"
                    height="494"
                  />
                </div>
              )}
              
              {/* Label and Description */}
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-semibold text-neutral-800 mb-2">
                  {currentItem.label}
                </h3>
                {currentItem.desc && (
                  <p className="text-sm text-neutral-600">
                    {currentItem.desc}
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        {items.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md z-10"
              onClick={() => paginate(-1)}
              aria-label="Sabor anterior"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md z-10"
              onClick={() => paginate(1)}
              aria-label="Siguiente sabor"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}
      </div>

      {/* Dots Navigation
      {items.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {items.map((item, index) => (
            <button
              key={item.id}
              onClick={() => handleDotClick(index)}
              className={cn(
                "w-2.5 h-2.5 rounded-full transition-all duration-200",
                index === currentIndex
                  ? "bg-pastel-orange w-8"
                  : "bg-neutral-300 hover:bg-neutral-400"
              )}
              aria-label={`Ir a ${item.label}`}
            />
          ))}
        </div>
      )} */}

      {/* Mobile swipe hint */}
      {items.length > 1 && (
        <p className="text-xs text-center text-neutral-500 mt-2 sm:hidden">
          Deslizá para ver más sabores
        </p>
      )}

      {footer && (
        <div className="mt-3 rounded-lg border border-neutral-200 bg-white/90 p-3 shadow-sm">
          {footer}
        </div>
      )}
    </div>
  );
};

Carousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      desc: PropTypes.string,
      imageUrl: PropTypes.string,
    })
  ).isRequired,
  selectedId: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
  footer: PropTypes.node,
};

Carousel.defaultProps = {
  className: "",
  footer: null,
};
